import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import {
  Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Slider,
} from '@material-ui/core';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import CloseIcon from '@material-ui/icons/Close';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import GetAppIcon from '@material-ui/icons/GetApp';
import SaveOutlinedIcon from '@material-ui/icons/SaveOutlined';
import TuneIcon from '@material-ui/icons/Tune';
import { AutoSizer, List } from 'react-virtualized';
import { useDispatch, useSelector } from 'react-redux';
import {
  clearCategoryChanges,
  clearCategorySelection,
  discardCategoryChange,
  fetchMerchandisingCategories,
  fetchMerchandisingIndexState,
  saveMerchandisingCategoryChanges,
  setCategoryExpanded,
  setCategorySelection,
  stageCategoryChanges,
  stageCategoryClear,
} from '../state/merchandisingSlice';
import {
  PageHeader, PriorityLabel, SearchInput, SelectionCheckbox, StateBoundary,
} from './MerchandisingWorkbenchUi';
import { CapabilityNotice } from './MerchandisingStatus';
import { useLocalizedMessage } from '../../../util/useLocalizedMessage';
import { useMerchandisingWorkbenchCopy } from './useMerchandisingWorkbenchCopy';
import IndexStatusBar from './IndexStatusBar';

const values = [0.25, 0.5, 1, 2, 4];
const marks = values.map((_, value) => ({ value }));
const parseCsvRow = (line) => {
  const valuesInRow = [];
  let value = '';
  let quoted = false;
  for (let index = 0; index < line.length; index += 1) {
    const character = line[index];
    if (character === '"' && quoted && line[index + 1] === '"') {
      value += '"';
      index += 1;
    } else if (character === '"') {
      quoted = !quoted;
    } else if (character === ',' && !quoted) {
      valuesInRow.push(value.trim());
      value = '';
    } else {
      value += character;
    }
  }
  valuesInRow.push(value.trim());
  return valuesInRow;
};
const nearestIndex = (factor) => values.reduce((best, value, index) => (
  Math.abs(value - Number(factor)) < Math.abs(values[best] - Number(factor)) ? index : best
), 0);

const categoryLocalFactor = (categories, id) => {
  const row = categories.entities[id];
  if (!row) return 1;
  if (categories.pendingClears[id]) return row.ai_multiplier ?? row.auto_multiplier ?? 1;
  return categories.pendingSets[id]?.multiplier
    ?? row.manual_multiplier
    ?? row.local_multiplier
    ?? 1;
};

const categoryAncestorFactor = (categories, id) => {
  let current = categories.entities[id]?.parent_id;
  let factor = 1;
  const visited = new Set();
  while (current && !visited.has(current)) {
    visited.add(current);
    factor *= categoryLocalFactor(categories, current);
    current = categories.entities[current]?.parent_id;
  }
  return factor;
};

const categoryEffectiveFactor = (categories, id) => Math.min(
  10,
  Math.max(0.1, categoryAncestorFactor(categories, id) * categoryLocalFactor(categories, id)),
);

const visibleCategoryIds = ({ entities, childrenByParent, expandedIds, order }, filter) => {
  const normalized = filter.trim().toLowerCase();
  if (normalized) {
    const included = new Set();
    order.forEach((id) => {
      const row = entities[id];
      if (!`${row?.name || ''} ${id}`.toLowerCase().includes(normalized)) return;
      let current = id;
      while (current && !included.has(current)) {
        included.add(current);
        current = entities[current]?.parent_id;
      }
    });
    return order.filter((id) => included.has(id));
  }
  const expanded = new Set(expandedIds);
  const result = [];
  const visit = (id) => {
    result.push(id);
    if (expanded.has(id)) (childrenByParent[id] || []).forEach(visit);
  };
  (childrenByParent.__root__ || []).forEach(visit);
  return result;
};

const CategoryPrioritiesWorkspace = () => {
  const dispatch = useDispatch();
  const t = useLocalizedMessage();
  const C = useMerchandisingWorkbenchCopy();
  const priorityLabels = [
    t('merchandising.workbench.priority.extraLow'),
    t('merchandising.workbench.priority.lower'),
    t('merchandising.workbench.priority.natural'),
    t('merchandising.workbench.priority.higher'),
    t('merchandising.workbench.priority.extraHigh'),
  ];
  const categories = useSelector((state) => state.merchandising.categories);
  const enabled = useSelector((state) => state.merchandising.capability.categories);
  const [filter, setFilter] = useState('');
  const [activeId, setActiveId] = useState(null);
  const [inspectorOpen, setInspectorOpen] = useState(false);
  const [bulkFactor, setBulkFactor] = useState(null);
  const [selectionAnchorId, setSelectionAnchorId] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [importText, setImportText] = useState('category_id,multiplier\n');
  const [importErrors, setImportErrors] = useState([]);

  const load = useCallback(() => dispatch(fetchMerchandisingCategories({
    languageId: null,
    includeImages: true,
  })), [dispatch]);
  useEffect(() => { if (enabled && !categories.order.length) load(); }, [categories.order.length, enabled, load]);
  useEffect(() => {
    if (!activeId && categories.order.length) setActiveId(categories.order[0]);
  }, [activeId, categories.order]);

  const visibleIds = useMemo(() => visibleCategoryIds(categories, filter), [categories, filter]);
  const active = categories.entities[activeId] || categories.entities[categories.order[0]];
  const selected = categories.selectedIds;
  const bulkMode = selected.length > 1;
  const selectedFingerprint = selected.join('\u001f');
  const commonBulkFactor = useMemo(() => {
    if (!selected.length) return null;
    const first = categoryLocalFactor(categories, selected[0]);
    return selected.every((id) => categoryLocalFactor(categories, id) === first) ? first : null;
  }, [categories, selected]);
  useEffect(() => {
    setBulkFactor(commonBulkFactor);
  }, [commonBulkFactor, selectedFingerprint]);
  useEffect(() => {
    if (!selected.length) setSelectionAnchorId(null);
  }, [selected.length]);
  const pendingCount = Object.keys(categories.pendingSets).length + Object.keys(categories.pendingClears).length;
  const recoveredCount = categories.recovery?.recoveredIds?.length || 0;
  const conflictCount = categories.recovery?.conflictIds?.length || 0;
  const conflictIds = categories.recovery?.conflictIds || [];
  const pendingClearCount = Object.keys(categories.pendingClears).length;
  const localFactor = active ? categoryLocalFactor(categories, active.id) : 1;
  const ancestorFactor = active ? categoryAncestorFactor(categories, active.id) : 1;
  const effective = active ? Math.round(categoryEffectiveFactor(categories, active.id) * 100) / 100 : 1;
  const descendants = useMemo(() => {
    const result = new Set();
    const visit = (id) => (categories.childrenByParent[id] || []).forEach((child) => {
      if (result.has(child)) return;
      result.add(child);
      visit(child);
    });
    selected.forEach(visit);
    return [...result];
  }, [categories.childrenByParent, selected]);
  const selectedScope = useMemo(
    () => [...new Set([...selected, ...descendants])],
    [descendants, selected],
  );
  const selectedScopeSet = useMemo(() => new Set(selectedScope), [selectedScope]);
  const indeterminateIds = useMemo(() => {
    const result = new Set();
    selected.forEach((id) => {
      let parentId = categories.entities[id]?.parent_id;
      while (parentId) {
        if (!selectedScopeSet.has(parentId)) result.add(parentId);
        parentId = categories.entities[parentId]?.parent_id;
      }
    });
    return result;
  }, [categories.entities, selected, selectedScopeSet]);

  const normalizeSelectionRoots = (ids) => {
    const unique = [...new Set(ids.map(String))];
    const candidates = new Set(unique);
    return unique.filter((id) => {
      let parentId = categories.entities[id]?.parent_id;
      while (parentId) {
        if (candidates.has(parentId)) return false;
        parentId = categories.entities[parentId]?.parent_id;
      }
      return true;
    });
  };

  const stage = (ids, factor) => dispatch(stageCategoryChanges(
    ids.map((categoryId) => ({ categoryId, multiplier: factor })),
  ));
  const commitBulkFactor = (index) => {
    const factor = values[index];
    setBulkFactor(factor);
    stage(selected, factor);
  };
  const replaceSelection = (id) => {
    dispatch(setCategorySelection([id]));
    setSelectionAnchorId(id);
    setActiveId(id);
    setInspectorOpen(true);
  };
  const toggleSelection = (id) => {
    const next = selected.includes(id)
      ? selected.filter((selectedId) => selectedId !== id)
      : (selectedScopeSet.has(id) ? [id] : [...selected, id]);
    dispatch(setCategorySelection(normalizeSelectionRoots(next)));
    setSelectionAnchorId(id);
    setActiveId(id);
    setInspectorOpen(true);
  };
  const extendSelection = (id, additive) => {
    const anchorIndex = visibleIds.indexOf(selectionAnchorId);
    const targetIndex = visibleIds.indexOf(id);
    if (anchorIndex < 0 || targetIndex < 0) {
      replaceSelection(id);
      return;
    }
    const start = Math.min(anchorIndex, targetIndex);
    const end = Math.max(anchorIndex, targetIndex);
    const range = visibleIds.slice(start, end + 1);
    dispatch(setCategorySelection(normalizeSelectionRoots(additive ? [...selected, ...range] : range)));
    setActiveId(id);
    setInspectorOpen(true);
  };
  const handleRowSelection = (id, event) => {
    const shiftKey = Boolean(event.shiftKey || event.nativeEvent?.shiftKey);
    const additive = Boolean(event.ctrlKey || event.metaKey || event.nativeEvent?.ctrlKey || event.nativeEvent?.metaKey);
    if (shiftKey) extendSelection(id, additive);
    else if (additive) toggleSelection(id);
    else replaceSelection(id);
  };
  const handleCheckboxSelection = (id, event) => {
    const shiftKey = Boolean(event.shiftKey || event.nativeEvent?.shiftKey);
    if (shiftKey) extendSelection(id, Boolean(event.ctrlKey || event.metaKey));
    else toggleSelection(id);
  };
  const save = async () => {
    const result = await dispatch(saveMerchandisingCategoryChanges({
      sets: Object.values(categories.pendingSets),
      clears: Object.values(categories.pendingClears),
    }));
    if (saveMerchandisingCategoryChanges.fulfilled.match(result)) {
      await load();
      dispatch(fetchMerchandisingIndexState({ languageId: null }));
    }
  };
  const exportCsv = () => {
    const lines = ['category_id,name,multiplier'];
    categories.order.forEach((id) => {
      const row = categories.entities[id];
      const multiplier = categories.pendingClears[id]
        ? ''
        : (categories.pendingSets[id]?.multiplier ?? row.manual_multiplier ?? '');
      lines.push(`${id},"${String(row.name).replace(/"/g, '""')}",${multiplier}`);
    });
    const url = URL.createObjectURL(new Blob([`${lines.join('\n')}\n`], { type: 'text/csv;charset=utf-8' }));
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'category-priorities.csv';
    anchor.click();
    URL.revokeObjectURL(url);
  };
  const importCsv = () => {
    const errors = [];
    const changes = [];
    importText.split(/\r?\n/).slice(1).filter((line) => line.trim()).forEach((line, index) => {
      const columns = parseCsvRow(line);
      const id = String(columns[0] || '');
      const factor = Number(columns.length > 2 ? columns[2] : columns[1]);
      if (!categories.entities[id]) errors.push(t('merchandising.workbench.csvCategoryMissing', { row: index + 2, id }));
      else if (!values.includes(factor)) errors.push(t('merchandising.workbench.csvMultiplierInvalid', { row: index + 2 }));
      else changes.push({ id, factor });
    });
    setImportErrors(errors);
    if (!errors.length) {
      dispatch(stageCategoryChanges(changes.map(({ id, factor }) => ({
        categoryId: id,
        multiplier: factor,
      }))));
      setImportOpen(false);
    }
  };

  const rowRenderer = ({ index, key, style }) => {
    const id = visibleIds[index];
    const row = categories.entities[id];
    const hasChildren = Boolean(categories.childrenByParent[id]?.length);
    const expanded = categories.expandedIds.includes(id);
    const selectedRow = selectedScopeSet.has(id);
    const indeterminateRow = indeterminateIds.has(id);
    const rowEffective = Math.round(categoryEffectiveFactor(categories, id) * 100) / 100;
    return (
      <div
        key={key}
        style={style}
        className={`mp-category-row ${activeId === id ? 'is-active' : ''}`}
        role="row"
        aria-level={Number(row.depth) + 1}
        aria-expanded={hasChildren ? expanded : undefined}
        aria-selected={selectedRow}
        tabIndex={0}
        data-testid={`category-row-${id}`}
        onMouseDown={(event) => {
          if ((event.ctrlKey || event.metaKey || event.shiftKey)
            && !event.target.closest('.mp-selection-checkbox')) event.preventDefault();
        }}
        onClick={(event) => {
          if (event.ctrlKey || event.metaKey || event.shiftKey) event.preventDefault();
          handleRowSelection(id, event);
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleRowSelection(id, event);
          }
          if (event.key === 'ArrowRight' && hasChildren && !expanded) {
            event.preventDefault();
            dispatch(setCategoryExpanded(id));
          }
          if (event.key === 'ArrowLeft' && hasChildren && expanded) {
            event.preventDefault();
            dispatch(setCategoryExpanded(id));
          }
        }}
      >
        <span role="gridcell" onClick={(event) => event.stopPropagation()}><SelectionCheckbox checked={selectedRow} indeterminate={indeterminateRow} label={t('merchandising.category.select', { name: row.name })} onChange={(event) => handleCheckboxSelection(id, event)} /></span>
        <span role="gridcell" className="mp-category-name" style={{ '--category-depth': row.depth }}>
          {hasChildren ? <IconButton aria-label={t(expanded ? 'merchandising.category.collapse' : 'merchandising.category.expand', { name: row.name })} onClick={(event) => { event.stopPropagation(); dispatch(setCategoryExpanded(id)); }}>{expanded ? <ExpandMoreIcon /> : <ChevronRightIcon />}</IconButton> : <span className="mp-category-indent" />}
          <span className="mp-category-identity"><strong>{row.name}</strong><small>#{id}</small></span>
        </span>
        <span role="gridcell" className="mp-tabular">{Number(row.product_count || 0).toLocaleString()}</span>
        <span role="gridcell" className="mp-tabular">{Number(row.subtree_product_count ?? row.product_count ?? 0).toLocaleString()}</span>
        <span role="gridcell"><PriorityLabel factor={rowEffective} copy={C} /></span>
      </div>
    );
  };
  const viewState = categories.request.status === 'loading' && !categories.order.length
    ? 'loading' : (categories.request.status === 'error' && !categories.order.length ? 'error' : (categories.order.length ? 'ready' : 'empty'));

  if (!enabled) {
    return <CapabilityNotice enabled={false}><span /></CapabilityNotice>;
  }

  const inspector = active && (
    <aside className={`mp-category-inspector ${inspectorOpen ? 'is-open' : ''}`} aria-label={t('merchandising.workbench.setPriority')}>
      <div className="mp-inspector-header"><div><span>{t(bulkMode ? 'merchandising.workbench.bulkEdit' : 'merchandising.workbench.setPriority')}</span><strong>{bulkMode ? t('merchandising.workbench.categoriesCount', { count: selectedScope.length }) : active.name}</strong></div><IconButton className="mp-inspector-close" aria-label={C.CLOSE} onClick={() => setInspectorOpen(false)}><CloseIcon /></IconButton></div>
      <div className="mp-category-inspector-body">
        <div className="mp-category-counts"><span><strong>{bulkMode ? selectedScope.reduce((sum, id) => sum + Number(categories.entities[id]?.product_count || 0), 0).toLocaleString() : Number(active.product_count || 0).toLocaleString()}</strong>{t('merchandising.workbench.directProducts')}</span><span><strong>{bulkMode ? selected.reduce((sum, id) => sum + Number(categories.entities[id]?.subtree_product_count ?? categories.entities[id]?.product_count ?? 0), 0).toLocaleString() : Number(active.subtree_product_count ?? active.product_count ?? 0).toLocaleString()}</strong>{t('merchandising.workbench.subtreeProducts')}</span></div>
        <div className="mp-priority-scale">
          <div className="mp-priority-scale-header"><span>{t('merchandising.workbench.lower')}</span><strong>{bulkMode && bulkFactor == null ? <span className="mp-priority-label mp-priority-label--normal">{t('merchandising.workbench.mixedPriority')}</span> : <PriorityLabel factor={bulkMode ? bulkFactor : localFactor} copy={C} />}</strong><span>{t('merchandising.workbench.higher')}</span></div>
          <Slider className={`mp-bipolar-slider ${bulkMode && bulkFactor == null ? 'is-mixed' : ''}`} min={0} max={4} step={1} marks={marks} value={nearestIndex(bulkMode && bulkFactor == null ? 1 : (bulkMode ? bulkFactor : localFactor))} onChange={(event, index) => bulkMode ? setBulkFactor(values[index]) : stage([active.id], values[index])} onChangeCommitted={(event, index) => { if (bulkMode) commitBulkFactor(index); }} valueLabelDisplay="auto" valueLabelFormat={(index) => `${values[index]}×`} aria-label={bulkMode ? t('merchandising.workbench.bulkCategoryPriority') : t('merchandising.workbench.priorityFor', { name: active.name })} />
          <div className="mp-scale-labels" aria-hidden="true">{priorityLabels.map((label, index) => <span key={label} style={{ '--mp-scale-position': `${index * 25}%` }}><strong>{label}</strong><small>{values[index]}{t('merchandising.workbench.multiplierSymbol')}</small></span>)}</div>
          {!bulkMode && <Button className="mp-reset-priority" disabled={!active.manual_rule_id && !categories.pendingSets[active.id]} onClick={() => dispatch(categories.pendingSets[active.id] && !active.manual_rule_id ? discardCategoryChange(active.id) : stageCategoryClear(active.id))}>{t('merchandising.workbench.resetAutomatic')}</Button>}
        </div>
        {!bulkMode && <div className="mp-priority-equation"><div><span>{t('merchandising.workbench.local')}</span><strong>{localFactor}{t('merchandising.workbench.multiplierSymbol')}</strong></div><span>{t('merchandising.workbench.multiplierSymbol')}</span><div><span>{t('merchandising.workbench.parents')}</span><strong>{Math.round(ancestorFactor * 100) / 100}{t('merchandising.workbench.multiplierSymbol')}</strong></div><span>=</span><div className="is-result"><span>{t('merchandising.workbench.result')}</span><strong>{effective}{t('merchandising.workbench.multiplierSymbol')}</strong></div></div>}
      </div>
    </aside>
  );

  return (
    <div>
      <PageHeader
        heading={C.CATEGORY_PRIORITIES}
        body={C.CATEGORY_DESCRIPTION}
        primaryLabel={pendingCount
          ? t('merchandising.category.saveChanges', { count: pendingCount })
          : t('merchandising.workbench.saveCategoryChanges')}
        primaryIcon={<SaveOutlinedIcon />}
        primaryDisabled={!pendingCount || categories.request.refreshing}
        onPrimary={save}
        secondary={<><Button variant="outlined" disabled={!pendingCount} onClick={() => dispatch(clearCategoryChanges())}>{C.RESET_CHANGES}</Button><Button variant="outlined" startIcon={<GetAppIcon />} onClick={exportCsv}>{t('merchandising.workbench.exportCsv')}</Button><Button variant="outlined" onClick={() => setImportOpen(true)}>{t('merchandising.workbench.importCsv')}</Button><IndexStatusBar inline /></>}
      />
      {categories.request.error && <div className="mp-inline-error" role="alert">{categories.request.error.message}</div>}
      {recoveredCount > 0 && conflictCount === 0 && (
        <div className="mp-inline-success" role="status">{t('merchandising.category.recovered', { count: recoveredCount })}</div>
      )}
      {conflictCount > 0 && (
        <div className="mp-inline-error" role="alert">
          {conflictIds.map((id) => (
            <div key={id}>{t('merchandising.overview.attention.categoryConflict', { id })}</div>
          ))}
        </div>
      )}
      {recoveredCount > 0 && conflictCount > 0 && pendingClearCount > 0 && (
        <div className="mp-inline-error" role="alert">{t('merchandising.category.partialClearFailure', { count: pendingClearCount })}</div>
      )}
      <StateBoundary state={viewState} onReady={load} copy={C} emptyActionLabel={C.RETRY}>
        <div className="mp-category-workspace">
          <main className="mp-category-tree-card">
            <div className="mp-category-toolbar"><SearchInput value={filter} onChange={setFilter} placeholder={t('merchandising.workbench.searchCategories')} /><span className="mp-category-result-meta">{t('merchandising.workbench.categoriesCount', { count: visibleIds.length.toLocaleString() })}</span><Button className="mp-mobile-inspector-trigger" variant="outlined" onClick={() => setInspectorOpen(true)} startIcon={<TuneIcon />}>{t('merchandising.workbench.setPriority')}</Button></div>
            <div className="mp-category-table" role="treegrid" aria-label={C.CATEGORY_PRIORITIES} data-testid="category-virtual-tree">
              <div className="mp-category-row mp-category-row--head" role="row"><span role="columnheader" /><span role="columnheader">{t('merchandising.workbench.category')}</span><span role="columnheader">{t('merchandising.workbench.directProducts')}</span><span role="columnheader">{t('merchandising.workbench.subtreeProducts')}</span><span role="columnheader">{t('merchandising.workbench.effectivePriority')}</span></div>
              <div className="mp-category-virtual-list"><AutoSizer>{({ height, width }) => <List height={height} width={width} rowCount={visibleIds.length} rowHeight={64} rowRenderer={rowRenderer} overscanRowCount={10} />}</AutoSizer></div>
            </div>
          </main>
          {inspector}
        </div>
        {selected.length > 0 && <button type="button" className="mp-clear-category-selection" onClick={() => dispatch(clearCategorySelection())}><CloseIcon />{t('merchandising.category.clearSelected')}</button>}
      </StateBoundary>
      <Dialog className="mp-dialog" open={importOpen} onClose={() => setImportOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('merchandising.workbench.importCategoryPriorities')}</DialogTitle>
        <DialogContent><textarea className="mp-csv-textarea" value={importText} onChange={(event) => setImportText(event.target.value)} aria-label={t('merchandising.workbench.categoryCsv')} />{importErrors.map((error) => <div key={error} className="mp-inline-error" role="alert">{error}</div>)}</DialogContent>
        <DialogActions><Button variant="outlined" onClick={() => setImportOpen(false)}>{t('merchandising.bulk.cancel')}</Button><Button variant="contained" color="primary" onClick={importCsv}>{t('merchandising.workbench.importChanges')}</Button></DialogActions>
      </Dialog>
    </div>
  );
};

export default CategoryPrioritiesWorkspace;
